import React, { useState } from 'react';
import { Button, Card, Modal, Space, Table } from 'antd';
import { DeviceCertificates, DeviceType, DeviceTypeCertificates } from '@services/types';
import { DeleteOutlined, ExclamationCircleOutlined, FormOutlined, InfoCircleOutlined } from '@ant-design/icons';
import CertificateInfoModal from './CertificateInfoModal';
import { toast } from 'react-toastify';
import CertificateUpdateModal from '@layouts/admin/Certificates/CertificateUpdateModal';
import api from '@services/api';
import { useParams } from 'react-router-dom';

const { Column } = Table;

const Certificates: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [addRulesModalVisibility, setAddRulesModalVisibility] = useState(false);
  const [certificates, setCertificates] = React.useState<DeviceCertificates[]>([]);
  const [deviceTypes, setDeviceTypes] = React.useState<DeviceType>();
  const [certificateInfo, setCertificateInfo] = React.useState<DeviceCertificates>();
  const [certificateUpdate, setCertificateUpdate] = React.useState<DeviceCertificates>();

  React.useEffect(() => {
    if (id) {
      Promise.all([api.administration.getDeviceTypeById(id), api.administration.getDeviceTypeCertificates(id)]).then(
        ([newDeviceType, newCertificates]) => {
          if (newDeviceType && newCertificates) {
            setDeviceTypes(newDeviceType);
            setCertificates(
              newCertificates.map((item) => ({
                ...item,
                deviceTypeName: newDeviceType?.deviceName,
              })),
            );
          }
        },
      );
    }
  }, [id]);

  const deleteConfirm = (certificate: DeviceTypeCertificates) => {
    Modal.confirm({
      title: 'Are you sure delete this certificate?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.administration.deleteDeviceTypeCertificate(id, certificate.id);
          setCertificates(certificates.filter(({ id }) => id !== certificate.id));
          toast.success('Certificate was deleted successfully');
        } catch (error) {
          console.log('Certificate was not deleted ~ error', error);
          toast.error('Certificate was not deleted');
        }
      },
    });
  };

  return (
    <Card
      title="Certificates"
      extra={
        <Button type="primary" onClick={() => setAddRulesModalVisibility(true)}>
          Add New Certificate
        </Button>
      }
      bodyStyle={{ padding: '5px' }}
    >
      <Table dataSource={certificates} rowKey={(record: DeviceCertificates) => record.id}>
        <Column title="Certificate" dataIndex="name" render={(text, record: DeviceCertificates) => record?.name} />
        <Column
          title="Common Name"
          dataIndex="etc.commonName"
          render={(text, record: DeviceCertificates) => record?.etc.commonName}
        />
        <Column
          title="Device Type"
          dataIndex="deviceTypeName"
          render={(text, record: DeviceCertificates) => record?.deviceTypeName}
        />
        <Column
          align="center"
          fixed="right"
          title="Action"
          key="action"
          render={(record: DeviceTypeCertificates) => (
            <Space size="middle">
              <InfoCircleOutlined onClick={() => setCertificateInfo(record)} />
              <FormOutlined onClick={() => setCertificateUpdate(record)} />
              <DeleteOutlined onClick={() => deleteConfirm(record)} />
            </Space>
          )}
        />
      </Table>
      <CertificateUpdateModal
        id={id}
        visible={addRulesModalVisibility}
        device={deviceTypes?.deviceName}
        setCertificates={setCertificates}
        onClose={() => {
          setAddRulesModalVisibility(false);
        }}
      />
      {certificateInfo && (
        <CertificateInfoModal
          certificate={certificateInfo}
          visible={!!certificateInfo}
          onClose={() => {
            setCertificateInfo(undefined);
          }}
        />
      )}
      {certificateUpdate && (
        <CertificateUpdateModal
          id={id}
          certificateUpdate={certificateUpdate}
          device={deviceTypes?.deviceName}
          setCertificates={setCertificates}
          visible={!!certificateUpdate}
          onClose={() => {
            setCertificateUpdate(undefined);
          }}
        />
      )}
    </Card>
  );
};
export default React.memo(Certificates);
